import Button from '@components/Button/Button';
import { EmailSignatureFieldTypes } from '@components/EmailSignaturev2/consts';
import {
  type EmailSignatureContactDetailsField,
  EmailSignatureContactDetailsFieldTypes,
  type EmailSignatureIconName,
  type EmailSignatureV2,
} from '@components/EmailSignaturev2/types';
import Input from '@components/Input/Input';
import useNode from '@hooks/use-node';
import useOnFocus from '@hooks/use-on-focus';
import useValidateInput, { emailValidator } from '@hooks/use-validate-input';
import {
  generateContactDetailsIconImage,
  generateNewEmailSignatureContactFieldId,
  measureTextWidth,
} from '@modules/email-signatures-helpers';
// es-toolkit/compat: lodash filter iterates objects/Records (not in es-toolkit main)
import { filter } from 'es-toolkit/compat';
import { useRouter } from 'next/router';
import { type FC, useMemo, useState } from 'react';

import {
  MobileOnboardingInputsGroup,
  MobileOnboardingInputsSection,
  MobileOnboardingInputsSectionHeader,
  MobileOnboardingInputsSectionHeaderContainer,
  MobileOnboardingNextButtonContainer,
} from './MobileOnboardingInputs';
import { type CommonOnboardingDetailsPageForMobileProps, MobileDetailsSteps } from './types';
import { deriveFieldWidth } from './utils';

type OnboardingContactDetailsPageForMobileProps = CommonOnboardingDetailsPageForMobileProps;

const OnboardingContactDetailsPageForMobile: FC<
  React.PropsWithChildren<OnboardingContactDetailsPageForMobileProps>
> = ({
  emailSignature,
  emailSignatureDispatch,
  handleSave,
  uploadEmailSignatureFieldImagesThunk,
  handleErrorUploadingImage,
}) => {
  const router = useRouter();
  const [emailInputNode, emailInputRef] = useNode();

  const onboardingEmail = useMemo(() => {
    const emails = filter(
      emailSignature.contactDetails,
      (emailSignatureContactDetailField) => emailSignatureContactDetailField.type === 'email',
    );

    // The onboarding email will always be the first one, because if someone signs up, add a new email
    // and comes back to this page, we will prompt them to create a new account or go to dashboard
    return emails.length > 0 ? emails[0] : null;
  }, [emailSignature.contactDetails]);

  const onboardingPhoneNumber = useMemo(() => {
    const phoneNumbers = filter(
      emailSignature.contactDetails,
      (emailSignatureContactDetailField) => emailSignatureContactDetailField.type === 'phone',
    );

    // The onboarding phone number will always be the first one, because if someone signs up, add a new phone number
    // and comes back to this page, we will prompt them to create a new account or go to dashboard
    return phoneNumbers.length > 0 ? phoneNumbers[0] : null;
  }, [emailSignature.contactDetails]);

  const [isEmailError, errorMessage] = useValidateInput(onboardingEmail?.value || '', [
    emailValidator,
  ]);
  const [, , emailInputBlurred] = useOnFocus(emailInputNode);
  const [showSubmitErrors, setShowSubmitErrors] = useState(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  function onContactDetailsFieldChange(
    fieldType: EmailSignatureContactDetailsFieldTypes,
    iconName: EmailSignatureIconName,
    updatedValue: string,
    currentField: EmailSignatureContactDetailsField | null,
  ) {
    if (!currentField) {
      const fieldIconImage = generateContactDetailsIconImage(
        iconName,
        emailSignature.design.themeColor,
      );
      if (!fieldIconImage) {
        return;
      }

      const newFieldId = generateNewEmailSignatureContactFieldId();
      uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) => {
        emailSignatureImagesUploader.uploadImage(
          newFieldId,
          fieldIconImage,
          EmailSignatureFieldTypes.CONTACT,
          handleErrorUploadingImage,
        );
      });

      const fieldTextWidth = measureTextWidth(updatedValue, emailSignature.design.font);
      emailSignatureDispatch({
        type: 'ADD_CONTACT_DETAIL_FIELD',
        value: {
          baseIcon: iconName,
          iconUrl: fieldIconImage,
          value: updatedValue,
          label: '',
          id: newFieldId,
          type: fieldType,
          fieldWidth: deriveFieldWidth(fieldTextWidth),
        },
      });
    } else {
      const fieldTextWidth = measureTextWidth(updatedValue, emailSignature.design.font);
      emailSignatureDispatch({
        type: 'UPDATE_CONTACT_DETAIL_FIELD_VALUE',
        value: { fieldId: currentField.id, updatedFieldValue: updatedValue },
      });
      emailSignatureDispatch({
        type: 'UPDATE_CONTACT_DETAIL_FIELD_WIDTH',
        value: {
          fieldId: currentField.id,
          updatedFieldValue: deriveFieldWidth(fieldTextWidth),
        },
      });
    }
  }

  async function onClickNext() {
    if (isEmailError) {
      emailInputNode?.focus();
      setShowSubmitErrors(true);
      return;
    }

    setIsSaving(true);
    await handleSave(emailSignature, (savedEmailSignature: EmailSignatureV2) => {
      router.push(
        `/onboarding/email-signature/details/${MobileDetailsSteps.SOCIAL_LINKS}`,
        undefined,
        {
          shallow: true,
        },
      );
      setIsSaving(false);
      emailSignatureDispatch({ type: 'IMPORT_EMAIL_SIGNATURE', value: savedEmailSignature });
    });
  }

  return (
    <MobileOnboardingInputsSection>
      <MobileOnboardingInputsSectionHeaderContainer>
        <MobileOnboardingInputsSectionHeader>
          Let&apos;s add your contact details.
        </MobileOnboardingInputsSectionHeader>
      </MobileOnboardingInputsSectionHeaderContainer>

      <MobileOnboardingInputsGroup>
        <Input
          label="Email"
          type="email"
          value={onboardingEmail ? onboardingEmail.value : ''}
          onChange={(updatedEmail) =>
            onContactDetailsFieldChange(
              EmailSignatureContactDetailsFieldTypes.EMAIL,
              'envelope',
              updatedEmail,
              onboardingEmail,
            )
          }
          autoComplete="email"
          ref={emailInputRef}
          errorMessage={onboardingEmail?.value ? errorMessage : 'Email address is required'}
          showError={
            (!!onboardingEmail?.value && isEmailError && emailInputBlurred) || showSubmitErrors
          }
          enableClearButton
        />

        <Input
          label="Phone number"
          type="tel"
          value={onboardingPhoneNumber ? onboardingPhoneNumber.value : ''}
          onChange={(updatedPhoneNumber) =>
            onContactDetailsFieldChange(
              EmailSignatureContactDetailsFieldTypes.PHONE,
              'phone-alt',
              updatedPhoneNumber,
              onboardingPhoneNumber,
            )
          }
          autoComplete="tel"
          enableClearButton
        />
      </MobileOnboardingInputsGroup>
      <MobileOnboardingNextButtonContainer>
        <Button size="large" onClick={onClickNext} loading={isSaving}>
          Next
        </Button>
      </MobileOnboardingNextButtonContainer>
    </MobileOnboardingInputsSection>
  );
};

export default OnboardingContactDetailsPageForMobile;
